import React, { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { toPng } from "html-to-image";
import CircuitLayout from "../../../components/CircuitLayout";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Button from "../../../components/Button";
import { Form, Col, Row, Alert } from "react-bootstrap";
import {
  faCalendar,
  faDotCircle,
  faDownload,
  faFlagCheckered,
  faLocationDot,
  faMapLocation,
  faMapLocationDot,
  faQuestionCircle,
  faStopwatch,
  faTrophy,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import DateFormat from "../../../components/DateFormat";
import LoadingContent from "../../../components/LoadingContent";
import Title from "../../../components/Admin/Title";
import { getAbbreviation } from "../../../utils/Standings";
import "../../../assets/style/imgnewsrally_v1.css";
import Flag from "../../../components/Flag";
import GeminiRequest from "../../../components/GeminiRequest";
import { processMilestoneAuto } from "../../../utils/Milestones";
import getTextColor from "../../../utils/DynamicContrast";

interface GPData {
  transformedStages: any;
  values: any;
  raceResults: any;
  driver_standings: any;
  team_standings: any;
  milestonesAuto: any;
}

const GPRallyGenerator = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<GPData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [customMilestones, setCustomMilestones] = useState<any>(null);
  const [generatedImageUrl, setGeneratedImageUrl] = useState<string | null>(
    null,
  );
  const componentRef = useRef<HTMLDivElement>(null);

  const [update, setUpdate] = useState(false);
  const [grandPrixEventId, setGrandPrixEventId] = useState(-1);
  const [nextGrandsPrix, setNextGrandsPrix] = useState<
    {
      name: string;
      id: number;
      type_race: string;
      type_races: number;
      type_championship: number;
    }[]
  >();

  const [customBgImages, setCustomBgImages] = useState<string[]>([]);
  const [imageError, setImageError] = useState<string | null>(null);
  const [geminiPromptText, setGeminiPromptText] = useState<string>("");

  const handleCustomImagesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    setImageError(null);

    if (files && files.length > 0) {
      const requiredCount = data ? data.transformedStages.length : 9;

      if (files.length !== requiredCount) {
        setImageError(
          `Debes seleccionar exactamente ${requiredCount} imágenes (has seleccionado ${files.length}).`,
        );
        setCustomBgImages([]);
        return;
      }

      const imageUrls = Array.from(files).map((file) =>
        URL.createObjectURL(file),
      );
      setCustomBgImages(imageUrls);
    } else {
      setCustomBgImages([]);
    }
  };

  useEffect(() => {
    axios
      .get(
        `${
          process.env.NODE_ENV === "development"
            ? "http://localhost:5500"
            : "https://ligavirtualf1.es:5500"
        }/api/grandprix/getLastGrandsPrix`,
        {
          params: { type_races: 1 },
        },
      )
      .then(({ data }) => {
        setNextGrandsPrix(data.nextGrandsPrix);
        setCustomMilestones(data.milestones);
      })
      .catch(({ response }) => {
        throw new Error(response);
      });
  }, [update]);

  useEffect(() => {
    if (
      grandPrixEventId !== null &&
      grandPrixEventId !== undefined &&
      grandPrixEventId !== -1
    ) {
      setIsLoading(true);
      setData(null);
      setGeneratedImageUrl(null);
      setError(null);
      setCustomBgImages([]);

      const API_BASE =
        process.env.NODE_ENV === "development"
          ? "http://localhost:5500"
          : "https://ligavirtualf1.es:5500";

      axios
        .get(`${API_BASE}/api/grandprix/getNewsRallyGenerateInfo`, {
          params: { grand_prix_event_id: grandPrixEventId },
        })
        .then(({ data }) => {
          setData(data);
          if (data.values.length === 0) setIsLoading(false);
        })
        .catch((err) => {
          console.error("Error fetching GP info:", err);
          setError("No se pudo cargar la información del Gran Premio.");
          setIsLoading(false);
        });
    }
  }, [grandPrixEventId]);
  // Reemplaza tus useEffects de generación de imagen actuales por estos:

  const handleGenerateImage = useCallback(() => {
    if (componentRef.current === null) return;

    // Pequeño timeout interno para asegurar el pintado final del DOM oculto
    setTimeout(() => {
      if (!componentRef.current) return;

      toPng(componentRef.current, {
        quality: 1.0,
        canvasWidth: 1920,
        canvasHeight: 1080,
        style: {
          overflow: "hidden",
        },
      })
        .then((dataUrl: string) => {
          setGeneratedImageUrl(dataUrl);
        })
        .catch((err) => {
          console.error("Error generating image:", err);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }, 600); // 600ms de margen para que CircuitLayout y las banderas terminen de pintar
  }, [componentRef]);

  useEffect(() => {
    if (data !== null && data.values.length > 0) {
      setIsLoading(true);
      const timer = setTimeout(() => {
        handleGenerateImage();
      }, 400); // Espera inicial al cambiar de datos antes de disparar

      return () => clearTimeout(timer);
    }
  }, [data, handleGenerateImage, customBgImages]);

  useEffect(() => {
    if (data !== null && data.values.length > 0) {
      const rafId = requestAnimationFrame(() => {
        handleGenerateImage();
      });
      return () => cancelAnimationFrame(rafId);
    }
  }, [data, handleGenerateImage, customBgImages]);

  useEffect(() => {
    if (data !== null) {
      const timer = setTimeout(() => {
        handleGenerateImage();
      }, 200);

      return () => clearTimeout(timer);
    }
  }, [data, handleGenerateImage]);

  const getWeatherIcon = (weatherString: string) => {
    if (weatherString == null) return "";
    const text = weatherString.toLowerCase();

    if (
      !text.includes("nosnow") &&
      (text.includes("heavysnow") ||
        text.includes("lightsnow") ||
        text.includes("snow"))
    ) {
      return "🌨️";
    }

    // 1. La lluvia va primero, pero bloqueando la palabra "norain" para que no salte por error
    if (
      !text.includes("norain") &&
      (text.includes("heavyrain") ||
        text.includes("lightrain") ||
        text.includes("rain"))
    ) {
      return "🌧️";
    }

    // 2. Las nubes van después. Si hay "HeavyRain HeavyCloud", como ya pasó el filtro de arriba, aquí cogerá la nube si no lloviera, pero si llueve se queda con la lluvia.
    if (
      text.includes("partcloud") ||
      text.includes("lightcloud") ||
      text.includes("heavycloud") ||
      text.includes("cloud")
    ) {
      return "☁️";
    }

    if (
      text.includes("heavyfog") ||
      text.includes("lightfog") ||
      text.includes("fog")
    ) {
      return "☁️";
    }
    if (text.includes("evening")) return "🌙";

    if (
      text.includes("crisp") ||
      text.includes("hazy") ||
      text.includes("clear")
    ) {
      return "☀️";
    }

    return "🌡️";
  };

  useEffect(() => {
    if (data && data.transformedStages) {
      // 1. Resumen de tramos, clases, tiempos y penalizaciones
      const stagesSummary = data.transformedStages
        .map((stage: any, index: number) => {
          const circuitData = data.values[index];
          const rallyData = Object.values(stage)[0] || {};

          return `
Tramo: ${circuitData.circuit} (Longitud: ${circuitData.length.toFixed(1)} km)
${Object.entries(rallyData)
  .map(
    ([className, drivers]: [string, any]) => `
  - Clase: ${className}
    ${drivers
      .map(
        (d: any) =>
          `     * Pos Clase: ${d.posGral} | Piloto: ${d.name} (${d.driver_flag_name}) - Coche: ${d.team} | Tiempo: ${d.race_time} | Gap Clase: ${d.gap} | Penalización: ${d.laps?.[0]?.penalty || 0}s | Sectores [S1-S2-S3]: ${d.laps?.[0]?.sectors ? Object.values(d.laps[0].sectors).join("-") : "N/A"}`,
      )
      .join("\n")}
`,
  )
  .join("")}
`;
        })
        .join("\n");

      // 2. Resumen de la clasificación general de la carrera (raceResults)
      // 2. Resumen de la clasificación general de la carrera (raceResults) con Posición de Clase calculada
      const raceResultsSummary = (() => {
        if (
          !data.raceResults ||
          !data.raceResults.R1 ||
          data.raceResults.R1.length === 0
        ) {
          return "No disponible";
        }

        // Objeto para llevar la cuenta de las posiciones por cada clase dinámicamente
        const classCounters: { [key: string]: number } = {};

        return data.raceResults.R1.map((res: any) => {
          const d = res[0];
          const className = d.class || "Desconocida";

          // Incrementamos la posición para esta clase específica
          classCounters[className] = (classCounters[className] || 0) + 1;
          const posClase = classCounters[className];

          return `- Pos Clase: ${posClase} | Clase: ${className} | Piloto: ${d.name} (${d.driver_flag_name}) | Coche: ${d.team} | Tiempo Total: ${d.race_time} | Gap: ${d.gap} | Puntos ${d.points}`;
        }).join("\n");
      })();

      // 3. Resumen del campeonato de pilotos (driver_standings)
      const driverStandingsSummary =
        data.driver_standings && data.driver_standings.length > 0
          ? data.driver_standings
              .map(
                (ds: any) =>
                  `- ${ds.position}. ${ds.drivers} - ${ds.points} puntos [Clase: ${ds.class}] -  Balance: ${ds.balance}`,
              )
              .join("\n")
          : "No disponible";

      // 4. Resumen del campeonato de equipos (team_standings)
      const teamStandingsSummary =
        data.team_standings && data.team_standings.length > 0
          ? data.team_standings
              .map(
                (ts: any) =>
                  `- ${ts.position}. ${ts.team_name} - ${ts.points} puntos [Clase: ${ts.class}] -  Balance: ${ts.balance}`,
              )
              .join("\n")
          : "No disponible";

      // 5. Construcción del prompt adaptado para Gemini
      const promptText = `
Actúa como un periodista deportivo especializado en rallies (simuladores como RBR / Richard Burns Rally). Tu objetivo es redactar una crónica de prensa atractiva, profesional y detallada sobre el rally disputado, basándote exclusivamente en los datos proporcionados.

- Nombre Oficial del Rally: ${data.values[0].gp_name} - Ronda ${data.values[0].round_number} de ${data.values[0].total_championship_events} del campeonato de ${data.values[0].champ_name}.

### Clasificación General Final de la Carrera (Race Results):
${raceResultsSummary}

### Resultados detallados por tramo, clase y sectores:
${stagesSummary}

### Clasificación General del Campeonato de Pilotos:
${driverStandingsSummary}

### Clasificación General del Campeonato de Equipos:
${teamStandingsSummary}

### Algunos datos del GP:
${
  customMilestones?.[grandPrixEventId]?.["es"]?.length > 0
    ? customMilestones[grandPrixEventId]["es"]
        .map((milestone: any) => `- ${milestone.description}`)
        .join("\n")
    : ""
}${
        data.milestonesAuto != null && data.milestonesAuto.length > 0
          ? "\n" +
            data.milestonesAuto
              .map(
                (milestone: any) => `- ${processMilestoneAuto(milestone, t)}`,
              )
              .join("\n")
          : ""
      }
  
### Pautas de Redacción:
- **Titular y Entradilla:** Crea un titular llamativo que resuma lo más destacado de la jornada y una entradilla que mencione el nombre del rally (${data.values[0].gp_name}) y los dominadores absolutos de la prueba.
- **Podio y Resultados Finales:** Utiliza los datos de "Race Results" para detallar quiénes subieron al podio absoluto de la prueba y cuáles fueron las diferencias finales.
- **Desarrollo por Categorías:** Analiza lo ocurrido en las clases usando los tramos, priorizando la posición por clase y los gaps.
- **Drama e Incidencias:** Presta atención al contador de SuperRallys (SRCounter) y a las penalizaciones de tiempo para relatar abandonos o errores clave.
- **Tiempos y Sectores:** Utiliza los datos de tramos, circuitos y sectores (s1, s2, s3) para explicar dónde se marcaron las diferencias.
- **Situación del Campeonato:** Dedica un apartado final a repasar cómo quedan las tablas de puntos tanto en el campeonato de pilotos como en el de equipos tras este rally analizando también el balance para saber si han ganado o perdido posiciones con respecto a la ronda anterior, (el balance comentalo para casos muy excepcionales, no lo hagas algo muy relevante). Ten en cuenta que este rally fue la ronda ${data.values[0].round_number} de ${data.values[0].total_championship_events}.
Con los Race Results tienes los puntos que consigue cada posición en la carrera (esto se mantiene para las rondas anteriores), con esto, puedes saber por ejemplo si un piloto ya era matemáticamente campeón antes de esta carrera o, si por ejemplo se ha proclamado campeón porque ya no pueden superarle de manera matemática (ya que sabes los puntos que gana el primero). Si no aparece algo referente a que se ha proclamado campeón un piloto en esta cita dentro del apartado de "Algunos datos del GP", no hagas alusión a ello ya eso significa que se ha coronado en algun otro Rally, puedes hacer alusión a que ya es campeón, no a que lo haya ganado aquí. 

Estructura requerida:
- Título: Máximo 60 caracteres (70 en casos muy extremos).
- Subtítulo: Alrededor de 100 caracteres, máximo 150.
- Cuerpo de la noticia: Completo y bien estructurado.

Lo necesito formateado con negritas en las partes importantes. Si me pones algun titulo/subtitulo en la noticia, utiliza el estilo de encabezado real de nivel 2. No me hagas enumeraciones con resultados o clasificaciones ya que debajo pondre un enlace a ellas.
    `.trim();

      setGeminiPromptText(promptText);
    }
  }, [data]);
  if (nextGrandsPrix == null) return <LoadingContent />;
  const totalStages = data ? data.transformedStages.length : 0;
  return (
    <div className="full-height padding-md app-container flex-center-top page-transition noverflow">
      <div className="form-container-md">
        <Title text="GP Rally Generator" />
        <small className="mandatory-fields">(*) Required fields</small>
        <Form>
          <Row className="mb-3">
            <Form.Group className="form-group" as={Col} md="12">
              <Form.Label>Grand Prix (*)</Form.Label>
              <Form.Select
                name="grand_prix"
                required
                onChange={(e) => {
                  setGrandPrixEventId(parseInt(e.target.value));
                }}
              >
                <option value="-1">Select a Grand Prix...</option>
                {nextGrandsPrix.map((gp) => {
                  return (
                    <option key={gp.id} value={gp.id}>
                      {gp.name}
                    </option>
                  );
                })}
              </Form.Select>
            </Form.Group>
          </Row>

          {data && (
            <Row className="mb-3">
              <Form.Group className="form-group" as={Col} md="12">
                <Form.Label>
                  Custom Background Images
                  <span
                    className="profile-bubble"
                    data-tooltip={`Upload exactly ${totalStages} images to customize each stage card background.`}
                    data-flow="right"
                  >
                    <FontAwesomeIcon
                      icon={faQuestionCircle}
                      className="label-icon"
                    />
                  </span>
                </Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleCustomImagesChange}
                />
                {imageError && (
                  <Alert variant="danger" className="mt-2 py-1">
                    {imageError}
                  </Alert>
                )}
              </Form.Group>
            </Row>
          )}
        </Form>
        {isLoading && <LoadingContent />}
        {data != null && data.values.length > 0 && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: "-9999px",
              pointerEvents: "none",
            }}
          >
            {/* Contenedor principal que envuelve TODO lo que se va a exportar en la imagen */}
            <div
              ref={componentRef}
              style={{ background: "#111", padding: "20px" }}
            >
              <div className="nig-container cont-r1">
                <div
                  className="nig-background-overlay"
                  style={
                    {
                      "--bg-gp-image": `url(/assets/ImagesGP/${data.values[0].id}.jpg)`,
                    } as React.CSSProperties
                  }
                />
              </div>

              <div className="dashboard-grid">
                {data.transformedStages.map((stageItem: any, index: any) => {
                  const stageDetails = {
                    name: `${data.values[index].circuit}`,
                    weather: `${getWeatherIcon(data.values[index].weather)} ${data.values[index].weather}`,
                    distance: `${data.values[index].length.toFixed(1)} km`,
                  };

                  const hasCustomImages = customBgImages.length === totalStages;

                  return (
                    <div
                      className="stage-card"
                      key={index}
                      style={{ position: "relative", overflow: "hidden" }}
                    >
                      {hasCustomImages && (
                        <div
                          style={{
                            position: "absolute",
                            top: "-10px",
                            left: "-10px",
                            right: "-10px",
                            bottom: "-10px",
                            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(${customBgImages[index]})`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            filter: "blur(2px)",
                            zIndex: 0,
                          }}
                        />
                      )}

                      <div
                        className="stage-header"
                        style={{ position: "relative", zIndex: 1 }}
                      >
                        <span className="stage-badge">
                          S {String(data.values[index].stage).padStart(2, "0")}
                        </span>
                        <div className="stage-info">
                          <h2>{stageDetails.name.toUpperCase()}</h2>
                          <p className="newsimg-weather">
                            {stageDetails.weather}{" "}
                          </p>
                        </div>
                      </div>

                      <div
                        className="stage-body"
                        style={{ position: "relative", zIndex: 1 }}
                      >
                        <div className="stage-map">
                          <div id="next-gp-container3">
                            <CircuitLayout
                              circuit_id={data.values[index].circuit_id}
                              id_canvas={`circuit-${data.values[index].circuit_id}`}
                              strokeWidth={2}
                              color={"#00e5ff"}
                            />
                          </div>
                          <span className="distance">
                            {stageDetails.distance}
                          </span>
                        </div>

                        <div className="stage-tables">
                          {Object.entries(data.transformedStages[index].R1).map(
                            (
                              [categoryName, drivers]: [string, any],
                              catIndex,
                            ) => (
                              <div className="table-column" key={catIndex}>
                                <h3
                                  className={`category-title ${categoryName.toLowerCase().replace(/\s+/g, "")}`}
                                  style={{
                                    backgroundColor: drivers[0].class_color,
                                    color: getTextColor(drivers[0].class_color),
                                  }}
                                >
                                  {categoryName.toUpperCase()}
                                </h3>
                                <ol>
                                  {(
                                    drivers as Array<{
                                      name: string;
                                      driver_flag: string;
                                    }>
                                  )
                                    .slice(0, 5)
                                    .map((driver, driverIndex) => (
                                      <li key={driverIndex}>
                                        <Flag
                                          flag_code={driver.driver_flag}
                                          name={""}
                                        />{" "}
                                        {getAbbreviation(driver.name)}
                                      </li>
                                    ))}
                                </ol>
                              </div>
                            ),
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
        {generatedImageUrl && (
          <div className="nig-preview-container mt-4">
            <img
              src={generatedImageUrl}
              alt="Preview Noticia GP"
              className="nig-preview-img"
              style={{ width: "100%", height: "auto", borderRadius: "8px" }}
            />
            <div className="mt-3">
              <Button
                text="Download Final Image"
                icon={faDownload}
                iconPosition="left"
                onClick={() => {
                  const link = document.createElement("a");
                  link.href = generatedImageUrl;
                  link.download = "GP_Results.png";
                  link.click();
                }}
              />
            </div>
          </div>
        )}
        {generatedImageUrl && <GeminiRequest text={geminiPromptText} />}
      </div>
    </div>
  );
};

export default GPRallyGenerator;
